import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Truck, Plane, Bike, MapPin, Navigation } from "lucide-react";
import { CITY_COORDINATES } from "../data/shipmentData";

export default function InteractiveMap({ shipment }) {
  const [activePointIndex, setActivePointIndex] = useState(null);
  const [simulatedProgress, setSimulatedProgress] = useState(0);

  // Linear projection from India Geo coordinates to SVG container dimensions (500x500)
  const project = (lat, lng) => {
    // Roughly India geographical limits
    const minLng = 67.0;
    const maxLng = 98.0;
    const minLat = 6.0;
    const maxLat = 37.0;

    const x = ((lng - minLng) / (maxLng - minLng)) * 480 + 10;
    // Invert Y coordinate for SVG
    const y = 500 - (((lat - minLat) / (maxLat - minLat)) * 480 + 10);
    return { x, y };
  };

  // Run a cute animation loop for the truck/flight icon moving along the path
  useEffect(() => {
    setSimulatedProgress(0);
    const interval = setInterval(() => {
      setSimulatedProgress((prev) => {
        if (prev >= 100) {
          return 0; // loop back
        }
        return prev + 1.5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [shipment?.awb]);

  // If no shipment is tracked, show general JPSY logistic network overview
  const isTracking = !!shipment;

  // Calculate current vehicle location based on simulatedProgress on the route path
  let vehicleCoords = { x: 250, y: 250 };
  let currentSegmentName = "";

  if (isTracking && shipment.routeCoordinates.length > 1) {
    const coordsCount = shipment.routeCoordinates.length;
    const segmentLength = 100 / (coordsCount - 1);
    const currentSegmentIndex = Math.min(
      Math.floor(simulatedProgress / segmentLength),
      coordsCount - 2,
    );

    const startPt = shipment.routeCoordinates[currentSegmentIndex];
    const endPt = shipment.routeCoordinates[currentSegmentIndex + 1];

    if (startPt && endPt) {
      const startProj = project(startPt.lat, startPt.lng);
      const endProj = project(endPt.lat, endPt.lng);

      const segmentProgress =
        (simulatedProgress % segmentLength) / segmentLength;

      vehicleCoords = {
        x: startProj.x + (endProj.x - startProj.x) * segmentProgress,
        y: startProj.y + (endProj.y - startProj.y) * segmentProgress,
      };

      currentSegmentName = `${startPt.name} → ${endPt.name}`;
    }
  }

  // Determine vehicle icon
  const getVehicleIcon = () => {
    if (!shipment) return <Truck className="w-5 h-5 text-white" />;
    switch (shipment.transportType) {
      case "flight":
        return <Plane className="w-5 h-5 text-white transform rotate-45" />;
      case "bike":
        return <Bike className="w-5 h-5 text-white" />;
      default:
        return <Truck className="w-5 h-5 text-white" />;
    }
  };

  return (
    <div
      id="jpsy-interactive-map"
      className="bg-slate-950 border border-slate-850 rounded-2xl p-5 shadow-xl text-slate-100 flex flex-col h-full min-h-[580px] relative overflow-hidden"
    >
      {/* Map Header */}
      <div className="flex justify-between items-center mb-4 z-10">
        <div>
          <h3 className="font-sans font-semibold tracking-tight text-lg text-slate-100 flex items-center gap-2">
            <Navigation className="w-5 h-5 text-blue-500 animate-pulse" />
            JPSY Maps{" "}
            <span className="text-[10px] uppercase font-mono tracking-widest text-blue-400 bg-blue-950 px-2 py-0.5 rounded-full border border-blue-900/50">
              Beta
            </span>
          </h3>
          <p className="text-xs text-slate-400">
            {isTracking
              ? `Real-time Route: ${shipment.originCity} to ${shipment.destinationCity}`
              : "Global Multi-Modal Cargo Network Overview"}
          </p>
        </div>

        {isTracking && (
          <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-950/40 border border-emerald-900/50 px-3 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
            <span>Live Tracking Active</span>
          </div>
        )}
      </div>

      {/* Main Map Box */}
      <div className="relative flex-1 bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden flex items-center justify-center p-2 min-h-[400px]">
        {/* Decorative Grid Lines */}
        <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px] opacity-40" />

        {/* Dynamic Map SVG */}
        <svg
          viewBox="0 0 500 500"
          className="w-full h-full max-h-[450px] object-contain relative z-10 transition-all duration-300"
        >
          {/* India Boundary Visual Guide (Stylized Abstract Dots/Lines for premium look) */}
          <path
            d="M 230 45 L 250 35 L 260 50 L 270 65 L 280 85 L 300 95 L 310 110 L 325 125 L 350 145 L 380 155 L 400 170 L 420 200 L 410 215 L 390 220 L 375 240 L 370 265 L 385 285 L 410 290 L 405 310 L 380 325 L 355 315 L 340 330 L 310 360 L 290 390 L 275 425 L 265 450 L 250 480 L 245 480 L 235 440 L 220 400 L 210 370 L 195 340 L 190 310 L 180 290 L 165 270 L 155 250 L 130 240 L 115 225 L 125 210 L 150 205 L 165 190 L 160 170 L 180 145 L 190 120 L 205 90 L 210 70 Z"
            fill="none"
            stroke="#1e293b"
            strokeWidth="2.5"
            strokeDasharray="5,5"
            className="opacity-40"
          />

          {/* BACKGROUND JPSY ROUTING NETWORK (When not tracking a specific item, or underneath) */}
          {!isTracking &&
            Object.entries(CITY_COORDINATES).map(([cityName, coords]) => {
              const startProj = project(coords.lat, coords.lng);
              // Draw connections to New Delhi (DEL) and Bangalore (BLR) as hubs
              const delProj = project(
                CITY_COORDINATES["New Delhi"].lat,
                CITY_COORDINATES["New Delhi"].lng,
              );
              const blrProj = project(
                CITY_COORDINATES["Bangalore"].lat,
                CITY_COORDINATES["Bangalore"].lng,
              );

              return (
                <g key={`net-${cityName}`} className="opacity-30">
                  <line
                    x1={startProj.x}
                    y1={startProj.y}
                    x2={delProj.x}
                    y2={delProj.y}
                    stroke="#3b82f6"
                    strokeWidth="1"
                    strokeDasharray="3,6"
                  />

                  <line
                    x1={startProj.x}
                    y1={startProj.y}
                    x2={blrProj.x}
                    y2={blrProj.y}
                    stroke="#10b981"
                    strokeWidth="1"
                    strokeDasharray="3,6"
                  />
                </g>
              );
            })}

          {/* ACTIVE ROUTE LINE */}
          {isTracking && shipment.routeCoordinates.length > 1 && (
            <>
              {/* Completed Path */}
              <path
                d={`M ${shipment.routeCoordinates
                  .map((p) => {
                    const proj = project(p.lat, p.lng);
                    return `${proj.x} ${proj.y}`;
                  })
                  .join(" L ")}`}
                fill="none"
                stroke="#1e293b"
                strokeWidth="4"
                strokeLinecap="round"
              />

              {/* Glowing active path line */}
              <motion.path
                d={`M ${shipment.routeCoordinates
                  .map((p) => {
                    const proj = project(p.lat, p.lng);
                    return `${proj.x} ${proj.y}`;
                  })
                  .join(" L ")}`}
                fill="none"
                stroke="url(#routeGradient)"
                strokeWidth="3.5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </>
          )}

          {/* Gradient definitions */}
          <defs>
            <linearGradient
              id="routeGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="50%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
            <radialGradient id="glowEffect" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* STATIC NETWORK CITIES OR ROUTE WAYPOINTS */}
          {isTracking
            ? shipment.routeCoordinates.map((pt, index) => {
                const proj = project(pt.lat, pt.lng);
                const isCurrent =
                  shipment.currentLocation.name
                    .toLowerCase()
                    .includes(pt.name.toLowerCase()) ||
                  (index === 1 && shipment.status === "in_transit");

                return (
                  <g
                    key={`point-${pt.name}-${index}`}
                    className="cursor-pointer"
                    onClick={() => setActivePointIndex(index)}
                  >
                    {/* Pulsing glow ring for current location */}
                    {isCurrent && (
                      <circle
                        cx={proj.x}
                        cy={proj.y}
                        r="18"
                        fill="url(#glowEffect)"
                        className="animate-pulse"
                      />
                    )}

                    <circle
                      cx={proj.x}
                      cy={proj.y}
                      r={isCurrent ? "8" : "5"}
                      fill={pt.isCompleted ? "#10b981" : "#475569"}
                      stroke={isCurrent ? "#3b82f6" : "#0f172a"}
                      strokeWidth="2"
                    />

                    {/* Text labels for city code */}
                    <text
                      x={proj.x}
                      y={proj.y - 12}
                      textAnchor="middle"
                      fill={isCurrent ? "#3b82f6" : "#94a3b8"}
                      fontSize="9"
                      fontWeight={isCurrent ? "bold" : "normal"}
                      className="font-mono tracking-wider drop-shadow-md select-none pointer-events-none"
                    >
                      {pt.name.split(" ")[0]}
                    </text>
                  </g>
                );
              })
            : Object.entries(CITY_COORDINATES).map(([cityName, coords]) => {
                const proj = project(coords.lat, coords.lng);
                return (
                  <g
                    key={`pin-${cityName}`}
                    className="group cursor-pointer"
                    onClick={() => {
                      // Click to view mock city details
                    }}
                  >
                    <circle
                      cx={proj.x}
                      cy={proj.y}
                      r="4.5"
                      fill="#2563eb"
                      stroke="#1e1b4b"
                      strokeWidth="1.5"
                      className="group-hover:fill-blue-400 transition-colors"
                    />

                    <text
                      x={proj.x}
                      y={proj.y - 10}
                      textAnchor="middle"
                      fill="#64748b"
                      fontSize="8"
                      className="font-mono opacity-80 group-hover:opacity-100 select-none pointer-events-none"
                    >
                      {coords.label}
                    </text>
                  </g>
                );
              })}

          {/* MOVING VEHICLE SCENE */}
          {isTracking && (
            <g>
              <circle
                cx={vehicleCoords.x}
                cy={vehicleCoords.y}
                r="14"
                fill="#2563eb"
                className="opacity-20 animate-ping"
              />

              <foreignObject
                x={vehicleCoords.x - 12}
                y={vehicleCoords.y - 12}
                width="24"
                height="24"
                className="overflow-visible"
              >
                <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center border-2 border-slate-900 shadow-lg text-white">
                  {getVehicleIcon()}
                </div>
              </foreignObject>
            </g>
          )}
        </svg>

        {/* Dynamic bottom status bar */}
        {isTracking && (
          <div className="absolute bottom-3 left-3 right-3 bg-slate-950/90 border border-slate-800 rounded-lg p-2.5 flex items-center justify-between text-xs backdrop-blur-sm shadow-md">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-blue-950 border border-blue-900/50 text-blue-400">
                {shipment.transportType === "flight" && (
                  <Plane className="w-3.5 h-3.5" />
                )}
                {shipment.transportType === "truck" && (
                  <Truck className="w-3.5 h-3.5" />
                )}
                {shipment.transportType === "bike" && (
                  <Bike className="w-3.5 h-3.5" />
                )}
              </div>
              <div>
                <p className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">
                  Active Leg
                </p>
                <p className="font-medium text-slate-200">
                  {currentSegmentName || "Preparing Transit"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">
                Simulated ETA
              </p>
              <p className="font-semibold text-blue-400">
                {shipment.estimatedDelivery}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Map Interactive Details drawer */}
      <AnimatePresence>
        {activePointIndex !== null && shipment && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            className="mt-4 p-3 bg-slate-900 border border-slate-800 rounded-xl relative flex flex-col gap-1 z-10"
          >
            <button
              onClick={() => setActivePointIndex(null)}
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-100 text-xs font-semibold px-1.5 py-0.5 rounded hover:bg-slate-800"
            >
              ×
            </button>
            <h4 className="font-sans font-semibold text-xs text-blue-400 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {shipment.routeCoordinates[activePointIndex].name}
            </h4>
            <p className="text-xs text-slate-300">
              {shipment.routeCoordinates[activePointIndex].isCompleted ? (
                <span className="text-emerald-400 font-medium">
                  ✓ Passed Hub Check
                </span>
              ) : (
                <span className="text-slate-400 font-medium">
                  ⏳ Scheduled transit check
                </span>
              )}
            </p>
            <p className="text-[11px] text-slate-400">
              This terminal is one of JPSY's primary automated sortation points.
              Parcels on this route undergo 2D barcode sorting and automated
              dimension screening.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Map Legend */}
      <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] text-slate-400 border-t border-slate-900 pt-3">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span>Completed Hubs</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-600" />
          <span>Active Transit</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-slate-600" />
          <span>Awaiting Arrival</span>
        </div>
      </div>
    </div>
  );
}
